/*
  herlamora-product-interest.js — meldet wiederholtes Interesse an einer
  Produktfarbe an Klaviyo.

  Gezaehlt wird nur im Browser (localStorage): jede Ansicht, bei der die
  Seite laenger als die eingestellte Dauer SICHTBAR war, gilt als Besuch.
  Kommen im Zeitfenster genug solcher Besuche derselben Produktfarbe
  zusammen, geht EINE Meldung raus -- danach ist diese Farbe bis zum Ende
  des Fensters erledigt.

  Zwei Wege zu Klaviyo, bewusst in dieser Reihenfolge:
  1. Eingeloggt: ueber den App-Proxy, serverseitig mit dem privaten Key.
     Adblocker und Tracking-Schutz blocken das Klaviyo-Skript regelmaessig,
     eine Anfrage an die eigene App kommt dagegen immer durch. Die E-Mail
     loest die App aus der Login-Session auf, der Browser schickt sie nie.
  2. Sonst: Klaviyo-Onsite-JS. Das trifft das Profil, das Klaviyo ueber
     seinen Cookie kennt (Newsletter-Anmeldung, Klick aus einer Mail) --
     genau der Fall, den eine Erinnerungsmail braucht.
*/
(function () {
  'use strict';

  var STORAGE_KEY = 'herlamora:product-interest:v1';
  var PROXY_URL = '/apps/herlamora-horses/product-interest';
  var TICK_MS = 1000;
  var LOG_PREFIX = '[Herlamora Produkt-Interesse]';

  function parseJson(value, fallback) {
    if (!value) return fallback;
    try {
      var parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed : fallback;
    } catch (error) {
      return fallback;
    }
  }

  function readStore() {
    try {
      return parseJson(window.localStorage.getItem(STORAGE_KEY), {});
    } catch (error) {
      /* Privates Fenster oder gesperrter Speicher: dann wird eben nicht
         gezaehlt, die Seite selbst darf daran nicht haengen. */
      return null;
    }
  }

  function writeStore(store) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
      return true;
    } catch (error) {
      return false;
    }
  }

  /* Ohne Einwilligung geht nichts raus. Fehlt die Shopify-API (kein
     Consent-Tool im Einsatz), bleibt es beim Verhalten des uebrigen
     Shops. */
  function trackingAllowed() {
    var api = window.Shopify && window.Shopify.customerPrivacy;
    if (!api || typeof api.marketingAllowed !== 'function') return true;

    try {
      return api.marketingAllowed() !== false;
    } catch (error) {
      return true;
    }
  }

  function klaviyoTrack(metric, properties) {
    var klaviyo = window.klaviyo || window._learnq;
    if (!klaviyo) return false;

    try {
      if (typeof klaviyo.track === 'function') {
        klaviyo.track(metric, properties);
      } else if (typeof klaviyo.push === 'function') {
        klaviyo.push(['track', metric, properties]);
      } else {
        return false;
      }
      return true;
    } catch (error) {
      return false;
    }
  }

  class HerlamoraProductInterest extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;

      this.productId = String(this.dataset.productId || '');
      if (!this.productId || !trackingAllowed()) return;

      this.colors = parseJson(this.dataset.colors, {});
      this.variantColors = parseJson(this.dataset.variantColors, {});
      this.dwellMs = (Number(this.dataset.dwellSeconds) || 15) * 1000;
      this.windowMs = (Number(this.dataset.windowDays) || 7) * 86400000;
      this.minVisits = Math.max(1, Number(this.dataset.minVisits) || 2);
      this.metric = this.dataset.metric || 'Herlamora Product Interest';
      this.loggedIn = this.dataset.loggedIn === 'true';

      this.color = this.colorOf(this.dataset.variantId);
      this.visibleMs = 0;
      this.qualified = false;
      this.lastTick = document.hidden ? 0 : Date.now();

      this.onVisibility = this.handleVisibility.bind(this);
      document.addEventListener('visibilitychange', this.onVisibility);
      window.addEventListener('pagehide', this.onVisibility);

      this.timer = setInterval(this.tick.bind(this), TICK_MS);

      /* Farbwechsel auf derselben Seite zaehlt als eigene Ansicht: die
         bisherige Zeit gehoert zur alten Farbe, die neue faengt bei 0 an. */
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
        try {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
        } catch (error) {
          /* Theme ohne PubSub: dann bleibt es bei der Startfarbe. */
        }
      }
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      document.removeEventListener('visibilitychange', this.onVisibility);
      window.removeEventListener('pagehide', this.onVisibility);
      if (this.unsubscribe) this.unsubscribe();
    }

    colorOf(variantId) {
      var mapped = this.variantColors[String(variantId || '').split('/').pop()];
      return typeof mapped === 'string' ? mapped : '';
    }

    handleVariantChange(event) {
      var variant = event && event.data && event.data.variant;
      if (!variant) return;

      var color = this.colorOf(variant.id);
      if (color === this.color) return;

      this.color = color;
      this.visibleMs = 0;
      this.qualified = false;
      this.lastTick = document.hidden ? 0 : Date.now();
    }

    handleVisibility() {
      if (document.hidden) {
        this.tick();
        this.lastTick = 0;
      } else {
        this.lastTick = Date.now();
      }
    }

    /* Nur sichtbare Zeit zaehlt -- ein Tab im Hintergrund ist kein
       Interesse. */
    tick() {
      if (!this.lastTick) return;

      var now = Date.now();
      this.visibleMs += now - this.lastTick;
      this.lastTick = document.hidden ? 0 : now;

      if (!this.qualified && this.visibleMs >= this.dwellMs) {
        this.qualified = true;
        this.record();
      }
    }

    key() {
      return this.productId + '|' + this.color.trim().toLowerCase();
    }

    /* Haelt den Eintrag dieser Farbe aktuell und wirft alles raus, was aus
       dem Zeitfenster gefallen ist. */
    record() {
      var store = readStore();
      if (!store) return;

      var now = Date.now();
      var oldest = now - this.windowMs;
      var key = this.key();

      Object.keys(store).forEach(function (entry) {
        var views = (store[entry].views || []).filter(function (time) {
          return time > oldest;
        });

        if (views.length === 0 && !(store[entry].sentAt > oldest)) {
          delete store[entry];
        } else {
          store[entry].views = views;
        }
      });

      var entry = store[key] || { views: [], seconds: 0 };
      entry.views.push(now);
      entry.seconds = Math.round((entry.seconds || 0) + this.visibleMs / 1000);
      store[key] = entry;
      writeStore(store);

      if (entry.views.length < this.minVisits) return;
      if (entry.sentAt && entry.sentAt > oldest) return;

      this.send(entry, now);
    }

    properties(entry, now) {
      var details = this.colors[this.color] || this.colors[''] || {};

      return {
        ProductID: Number(this.productId) || this.productId,
        ProductName: this.dataset.productTitle || '',
        ProductHandle: this.dataset.productHandle || '',
        ProductURL: details.url || this.dataset.productUrl || '',
        ImageURL: details.image || '',
        Price: typeof details.price === 'number' ? details.price : null,
        PriceText: details.price_text || '',
        Currency: this.dataset.currency || '',
        Vendor: this.dataset.productVendor || '',
        VariantID: details.variant || null,
        SKU: details.sku || '',
        Color: this.color,
        ViewCount: entry.views.length,
        DwellSeconds: entry.seconds,
        FirstViewedAt: new Date(entry.views[0]).toISOString(),
        LastViewedAt: new Date(now).toISOString(),
        WindowDays: Math.round(this.windowMs / 86400000),
      };
    }

    /* Die Kennung macht den Versand wiederholbar ohne Doppel-Mail: Klaviyo
       behaelt pro Metrik und Profil nur das erste Event mit dieser id. */
    uniqueId(entry) {
      return ['herlamora-product-interest', this.productId, this.color.trim().toLowerCase(), entry.views[0]]
        .join('-')
        .replace(/\s+/g, '-');
    }

    send(entry, now) {
      var properties = this.properties(entry, now);
      var uniqueId = this.uniqueId(entry);
      var self = this;

      var done = function () {
        var store = readStore();
        if (!store || !store[self.key()]) return;
        store[self.key()].sentAt = Date.now();
        writeStore(store);
      };

      if (!this.loggedIn) {
        if (klaviyoTrack(this.metric, properties)) done();
        return;
      }

      fetch(PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ metric: this.metric, uniqueId: uniqueId, properties: properties }),
        keepalive: true,
      })
        .then(function (response) {
          if (response.ok) {
            done();
            return;
          }

          /* App nicht erreichbar oder Kunde ohne E-Mail: dann wenigstens
             ueber das Onsite-Skript versuchen. */
          if (klaviyoTrack(self.metric, properties)) done();
        })
        .catch(function (error) {
          console.warn(LOG_PREFIX, 'Meldung fehlgeschlagen', error);
          if (klaviyoTrack(self.metric, properties)) done();
        });
    }
  }

  if (!customElements.get('herlamora-product-interest')) {
    customElements.define('herlamora-product-interest', HerlamoraProductInterest);
  }
})();
